import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { COUNTRIES } from 'constants/countries';
import type { PaymentOption } from 'modules/billing/types';
import { toCurrency } from 'store/accounts/helpers';
import { NetworkType } from 'store/proxies/types';
import type { Period, Option, PartialRecord, Nullable } from 'types';
import { formatRange, parseTimePeriod } from 'utils/values';

import { SERVICE_WEIGHTS } from './constants';
import type {
  CityAvailabilityMetadataDTO,
  CountryAvailabilityMetadataDTO,
  ISPAvailabilityMetadataDTO,
  OrderDTO,
  RegionAvailabilityMetadataDTO,
  OrderSetupExecuteDTO,
  OrderSetupPriceAppliedDiscountsDTO,
  OrderSetupPriceLabelsDTO,
  OrderSetupPriceSpecialOfferDTO,
  OrderSetupPriceWithMetadataDTO,
  OrderSetupWithMetadataDTO,
  PeriodAvailabilityMetadataDTO,
  PeriodPerCountryAvailabilityMetadataDTO,
  PlanDTO,
  ServiceAvailabilityMetadataDTO,
  ServicesWithMetadataDTO,
} from './dtos';
import type {
  OrderModel,
  OrderSetupCityField,
  OrderSetupCityFieldParameters,
  OrderSetupCountriesParameters,
  OrderSetupExecuteModel,
  OrderSetupFormModel,
  OrderSetupISPsField,
  OrderSetupISPsFieldParameters,
  OrderSetupModel,
  OrderSetupRegionField,
  OrderSetupPackagesParameters,
  OrderSetupPeriodsParameters,
  OrderSetupPriceLabelModel,
  OrderSetupPriceModel,
  OrderSetupPriceSpecialOfferModel,
  OrderSetupQuantityParameters,
  OrderSetupRegionFieldParameters,
  OrderSetupTrafficParameters,
  PlanModel,
  ServiceModel,
} from './models';
import type { OrderExecutePayload, OrderSetupPayload, PeriodPayload, ServiceDetailsPayload } from './payloads';
import type { BasePricing, IPQualityVendor, ServiceID } from './types';

// #region ISP helpers
export function isISPValidForCountry(
  country: string | undefined,
  ispOptions: OrderSetupISPsField['options'],
  isp: string | undefined,
) {
  if (!isp || !country) return false;

  return (ispOptions[country] ?? []).some((o) => o.value === isp);
}

export function isISPValidForRegion(
  country: string | undefined,
  region: string,
  regionOptions: OrderSetupRegionField['options'],
  isp: string | undefined,
) {
  if (!isp || !country) return false;

  const allowedIds = (regionOptions[country] ?? []).find((o) => o.value === region)?.parameters.isps;

  if (!allowedIds) return false;

  return allowedIds.includes(isp);
}

export function isISPValidForCity(
  country: string | undefined,
  city: string,
  cityOptions: OrderSetupCityField['options'],
  isp: string | undefined,
) {
  if (!isp || !country) return false;

  const allowedIds = (cityOptions[country] ?? []).find((o) => o.value === city)?.parameters.isps;

  if (!allowedIds) return false;

  return allowedIds.includes(isp);
}
// #endregion ISP helpers

// #region Utilities
function formatTimePeriod(from: Nullable<string>): PeriodPayload {
  const parsedTimePeriod = parseTimePeriod(from);

  if (!parsedTimePeriod) return { unit: 'months', value: 1 };

  const { format, value } = parsedTimePeriod;

  return { unit: format, value };
}

export function toNetworkType(from: ServiceID): NetworkType {
  if (from === 'rotating-mobile') {
    return NetworkType.Mobile;
  }

  if (from === 'rotating-residential') {
    return NetworkType.Residential;
  }

  if (from === 'static-datacenter-ipv4' || from === 'static-datacenter-ipv6') {
    return NetworkType.Datacenter;
  }

  if (from === 'static-residential-ipv4') {
    return NetworkType.ResidentialStatic;
  }

  return NetworkType.VPN;
}
// #endregion Utilities

// #endregion Mappers for DTOs

// #region Service and Plan models

function toPlanModel(plan: PlanDTO, availability?: ServiceAvailabilityMetadataDTO, pricing?: BasePricing): PlanModel {
  return { ...plan, pricing: pricing ?? null, isAvailable: !!availability?.plans[plan.id] };
}

export function toServiceModel({ services, metadata }: ServicesWithMetadataDTO): ServiceModel[] {
  return services
    .map<ServiceModel>(({ id, label, plans = [] }) => {
      return {
        id,
        label,
        plans: plans.map((plan) =>
          toPlanModel(plan, metadata.availability.services?.[id], metadata.pricing.services?.[id]?.plans?.[plan.id]),
        ),
        pricing: metadata.pricing.services[id],
        isOrderable: metadata.orderable.services[id],
        weight: SERVICE_WEIGHTS[id],
        isVisible: metadata.visibility.services[id],
      };
    })
    .filter(({ isVisible }) => isVisible)
    .sort((a, b) => a.weight - b.weight);
}
// #endregion Service and Plan models

// #region Execute model
export function toOrderSetupExecuteModel(from: OrderSetupExecuteDTO): OrderSetupExecuteModel {
  return from;
}
// #endregion Execute model

// #region Order model
export function toOrderModel(from: OrderDTO): OrderModel {
  return {
    id: from.id,
    status: from.status,
    isPaidWithCrypto: from.isPaidWithCrypto,
    periodInMonths: from.periodInMonths,
    periodInDays: from.periodInDays,
    totalPrice: from.totalPrice != null ? Number(from.totalPrice) : null,
    proxies: from.proxies?.map(({ id, createdAt, expiresAt, serviceId, planId, price }) => ({
      id,
      createdAt,
      expiresAt,
      serviceId,
      planId,
      price,
    })),
  };
}
// #endregion Order model

// #region Price model
function toSpecialOfferModel({
  discount,
  properties,
}: OrderSetupPriceSpecialOfferDTO): Nullable<OrderSetupPriceSpecialOfferModel> {
  if (properties.days) {
    return { property: 'days', discount, value: properties.days };
  }

  if (properties.months) {
    return { property: 'months', discount, value: properties.months };
  }

  if (properties.quantity) {
    return { property: 'quantity', discount, value: properties.quantity };
  }

  if (properties.traffic) {
    return { property: 'traffic', discount, value: properties.traffic };
  }

  if (properties.package_quantity) {
    return { property: 'package_quantity', discount, value: properties.package_quantity };
  }

  return null;
}

function toPriceLabelsModel(
  appliedDiscounts: OrderSetupPriceAppliedDiscountsDTO[],
  labels: OrderSetupPriceLabelsDTO,
): OrderSetupPriceLabelModel[] {
  return appliedDiscounts
    .map(({ id, value }) => {
      const currentDiscount = labels.appliedDiscounts?.[id];

      if (!currentDiscount) return null;

      return { id, discount: value, accentColor: currentDiscount.accentColor, label: currentDiscount.label };
    })
    .filter((v) => !isNil(v));
}

export function toOrderSetupPriceModel({
  appliedDiscounts,
  currency,
  metadata,
  available,
  ipQualityProviders,
  ...from
}: OrderSetupPriceWithMetadataDTO): OrderSetupPriceModel {
  const base = {
    ...from,
    currency: toCurrency(currency),
    isAvailable: available ?? true,
    ipQualityProviders: ipQualityProviders ?? [],
  };

  if (Array.isArray(metadata)) {
    return { ...base, specialOffer: null, labels: [], ipQualityFee: null };
  }

  return {
    ...base,
    specialOffer: !metadata.specialOffer ? null : toSpecialOfferModel(metadata.specialOffer),
    ipQualityFee: !metadata.ipQuality ? null : metadata.ipQuality.fee,
    labels: !metadata.labels ? [] : toPriceLabelsModel(appliedDiscounts, metadata.labels),
  };
}
// #endregion Price model

// #region Setup model
function toCountryOptionsModel(
  countries: string[],
  availability?: CountryAvailabilityMetadataDTO,
  pricing?: Record<string, BasePricing>,
): Array<Option<string, OrderSetupCountriesParameters>> {
  return countries
    .map((code) => {
      const country = COUNTRIES.find((c) => c.value === code);

      if (!country) return null;

      return {
        ...country,
        parameters: {
          availability: availability?.[code] != null ? formatRange(availability?.[code]) : null,
          pricing: pricing?.[code],
        },
      };
    })
    .filter((v) => !isNil(v));
}

function toRegionOptionsModel(
  regions: Record<string, Array<{ id: string; label: string; isps: string[] }>>,
  availability?: RegionAvailabilityMetadataDTO,
  pricing?: Record<string, Record<string, BasePricing>>,
): Record<string, Array<Option<string, OrderSetupRegionFieldParameters>>> {
  return Object.entries(regions).reduce<Record<string, Array<Option<string, OrderSetupRegionFieldParameters>>>>(
    (acc, [country, data]) => ({
      ...acc,
      [country]: data.map(({ id, label, isps }) => ({
        value: id,
        label,
        parameters: {
          availability: availability?.[country]?.[id] != null ? formatRange(availability?.[country][id]) : null,
          pricing: pricing?.[country]?.[id] ?? undefined,
          isps,
        },
      })),
    }),
    {},
  );
}

function toCityOptionsModel(
  cities: Record<string, Array<{ id: string; label: string; region?: string; isps: string[] }>>,
  availability?: CityAvailabilityMetadataDTO,
  pricing?: Record<string, Record<string, BasePricing>>,
): Record<string, Array<Option<string, OrderSetupCityFieldParameters>>> {
  return Object.entries(cities).reduce<Record<string, Array<Option<string, OrderSetupCityFieldParameters>>>>(
    (acc, [country, data]) => ({
      ...acc,
      [country]: data.map(({ id, label, region, isps }) => ({
        value: id,
        label,
        parameters: {
          availability: availability?.[country]?.[id] != null ? formatRange(availability?.[country][id]) : null,
          pricing: pricing?.[country]?.[id] ?? undefined,
          region,
          isps,
        },
      })),
    }),
    {},
  );
}

function toISPOptionsModel(
  isps: Record<string, Array<{ id: string; label: string }>>,
  availability?: ISPAvailabilityMetadataDTO,
  pricing?: Record<string, Record<string, BasePricing>>,
): Record<string, Array<Option<string, OrderSetupISPsFieldParameters>>> {
  return Object.entries(isps).reduce<Record<string, Array<Option<string, OrderSetupISPsFieldParameters>>>>(
    (acc, [country, data]) => ({
      ...acc,
      [country]: data.map(({ id, label }) => ({
        value: id,
        label,
        parameters: {
          availability: availability?.[country]?.[id] != null ? formatRange(availability?.[country][id]) : null,
          pricing: pricing?.[country]?.[id] ?? undefined,
        },
      })),
    }),
    {},
  );
}

function toPackageOptionsModel(
  from: Array<{ id: string; name: string }>,
  pricing?: Record<string, BasePricing>,
): Array<Option<string, OrderSetupPackagesParameters>> {
  if (!pricing) return [];

  return from.map(({ id }) => ({ label: id, value: id, parameters: { pricing: pricing[id] } }));
}

function toPeriodOptionsModel(
  periods: PartialRecord<Period, number[]>,
  serviceId: ServiceID,
  availability?: PeriodAvailabilityMetadataDTO,
  pricing?: Record<Period, Record<string, BasePricing>>,
): Record<Period, Array<Option<string, OrderSetupPeriodsParameters>>> {
  return Object.entries(periods).reduce<Record<Period, Array<Option<string, OrderSetupPeriodsParameters>>>>(
    (acc, [period, data]) => {
      if (period === 'days') {
        return {
          ...acc,
          [period as Period]: data.map((value) => {
            if (serviceId === 'dedicated-mobile') {
              return {
                value: `${value}d`,
                label: 'common:form.day',
                parameters: {
                  availability: availability?.[period as Period]?.[value.toString()],
                  pricing: pricing?.[period as Period]?.[value.toString()],
                },
              };
            }

            return {
              value: `${value}d`,
              label: value === 7 ? 'common:labels.trial.weekTrial' : 'common:form.day',
              parameters: {
                availability: availability?.[period as Period]?.[value.toString()],
                pricing: pricing?.[period as Period]?.[value.toString()],
              },
            };
          }),
        };
      }

      return {
        ...acc,
        [period as Period]: data.map((value) => ({
          value: `${value}m`,
          label: 'common:form.month',
          parameters: {
            availability: availability?.[period as Period]?.[value.toString()],
            pricing: pricing?.[period as Period]?.[value.toString()],
          },
        })),
      };
    },
    { days: [], months: [] },
  );
}

function isAvailable(value: number, check?: Record<number, boolean>) {
  if (!check) return false;

  return Object.keys(check).some((v) => isEqual(Number.parseInt(v, 10), value));
}

export function toPeriodOptionsPerCountryModel(
  periods: PartialRecord<Period, number[]>,
  availability?: PeriodPerCountryAvailabilityMetadataDTO,
  pricing?: Record<string, Record<Period, Record<string, BasePricing>>>,
): Record<string, Record<Period, Array<Option<string, OrderSetupPeriodsParameters>>>> {
  if (availability) {
    return Object.entries(availability ?? {}).reduce<
      Record<string, Record<Period, Array<Option<string, OrderSetupPeriodsParameters>>>>
    >((acc, [countryCode, data]) => {
      return {
        ...acc,
        [countryCode]: Object.entries(periods).reduce(
          (options, [period, currData]) => {
            if (period === 'days') {
              return {
                ...options,
                [period as Period]: currData
                  .filter((v) => isAvailable(v, data.days))
                  .map((value) => ({ value: `${value}d`, label: 'common:form.day' })),
              };
            }

            return {
              ...options,
              ...options,
              [period as Period]: currData
                .filter((v) => isAvailable(v, data.months))
                .map((value) => ({ value: `${value}m`, label: 'common:form.month' })),
            };
          },
          { days: [], months: [] },
        ),
      };
    }, {});
  }

  return Object.keys(pricing ?? {}).reduce<
    Record<string, Record<Period, Array<Option<string, OrderSetupPeriodsParameters>>>>
  >((acc, countryCode) => {
    return {
      ...acc,
      [countryCode]: Object.entries(periods).reduce(
        (options, [period, currData]) => {
          if (period === 'days') {
            return {
              ...options,
              [period as Period]: currData.map((value) => ({
                value: `${value}d`,
                label: 'common:form.day',
                parameters: {
                  pricing: pricing?.[countryCode]?.[period]?.[value] ?? null,
                },
              })),
            };
          }

          return {
            ...options,
            ...options,
            [period as Period]: currData.map((value) => ({
              value: `${value}m`,
              label: 'common:form.month',
              parameters: {
                pricing: pricing?.[countryCode]?.[period as Period]?.[value] ?? null,
              },
            })),
          };
        },
        { days: [], months: [] },
      ),
    };
  }, {});
}

function toQuantityOptionsModel(
  values: number[],
  pricing?: Record<string, BasePricing>,
): Array<Option<number, OrderSetupQuantityParameters>> {
  if (!pricing) return [];

  return values.map((value) => ({ label: value.toString(), value, parameters: { pricing: pricing[value] } }));
}

function toTrafficOptionsModel(
  values: number[],
  pricing?: Record<string, BasePricing>,
): Array<Option<number, OrderSetupTrafficParameters>> {
  if (!pricing) return [];

  return values.map((value) => ({ label: value.toString(), value, parameters: { pricing: pricing[value] } }));
}

function toIPQualityOptionsModel(
  values: Array<{ id: IPQualityVendor; label: string }> = [],
): Array<Option<IPQualityVendor>> {
  return values.map(({ id, label }) => ({ value: id, label }));
}

export function toOrderSetupModel({
  serviceId,
  countries = [],
  isps = {},
  packages = [],
  periods = {},
  cities = {},
  regions = {},
  metadata,
}: OrderSetupWithMetadataDTO): OrderSetupModel {
  const { availability, visibility, pricing } = metadata;

  return {
    autoExtend: {
      isVisible: !!visibility?.autoExtend,
    },
    autoExtendTraffic: {
      isVisible: !!visibility?.autoExtendTraffic,
      availability: availability?.autoExtend?.traffic ?? null,
    },
    countries: {
      isVisible: !!visibility?.countries,
      options: toCountryOptionsModel(countries, availability?.countries, pricing?.country),
    },
    regions: {
      isVisible: !!visibility?.regions,
      options: toRegionOptionsModel(regions, availability?.regions, pricing?.region),
    },
    cities: {
      isVisible: !!visibility?.cities,
      options: toCityOptionsModel(cities, availability?.cities, pricing?.city),
    },
    isps: {
      isVisible: !!visibility?.isps,
      options: toISPOptionsModel(isps, availability?.isps, pricing?.isp),
    },
    packages: {
      isVisible: !!visibility?.packages,
      options: toPackageOptionsModel(packages, pricing?.packages),
      availability: availability?.packages ?? null,
    },
    periods: {
      isVisible: !!visibility?.periods,
      options: toPeriodOptionsModel(periods, serviceId, availability?.periods, pricing?.periods),
      optionsPerCountry: toPeriodOptionsPerCountryModel(
        periods,
        availability?.periodsPerCountry,
        pricing?.periodsPerCountry,
      ),
    },
    plans: {
      isVisible: !!visibility?.plans,
    },
    quantity: {
      isVisible: !!visibility?.quantity,
      availability: availability?.quantity ?? null,
      options: toQuantityOptionsModel(availability?.quantity?.values ?? [], pricing?.quantity),
    },
    traffic: {
      isVisible: !!visibility?.traffic,
      availability: availability?.traffic ?? null,
      options: toTrafficOptionsModel(availability?.traffic?.values ?? [], pricing?.traffic),
    },
    subnet: {
      isVisible: false,
      availability: availability?.subnet
        ? { cidr: availability.subnet.cidr, amount: formatRange(availability.subnet.amount) }
        : null,
    },
    ipQuality: {
      isVisible: !!visibility?.ipQuality,
      options: toIPQualityOptionsModel(availability?.ipQuality?.vendors),
      scores: availability?.ipQuality?.scores ?? null,
      pricing: pricing?.ipQuality,
    },
  };
}
// #endregion Setup model

// #region Payloads
export function buildServiceDetailsPayload({ service, originProxyId, ...payload }: ServiceDetailsPayload) {
  if (!service.plans.length) return null;

  if ('planId' in payload) {
    return { planId: payload.planId, originProxyId };
  }

  return { country: payload.country, originProxyId };
}

export function toOrderSetupPayload(from: OrderSetupFormModel): OrderSetupPayload {
  const {
    serviceId,
    bandwidth,
    additionalBandwidth,
    region,
    city,
    // autoExtendBandwidth,
    isAutoExtendEnabled,
    isp,
    package: proxyPackage,
    period,
    planId,
    country,
    quantity,
    couponCode,
    originProxyId,
    ipQualityScore,
    ipQualityVendor,
  } = from;

  if (serviceId === 'vpn') throw new Error('VPN is not supported');

  if (serviceId === 'rotating-mobile' || serviceId === 'rotating-residential') {
    return { serviceId, traffic: bandwidth, couponCode, planId: null, originProxyId };
  }

  if (serviceId === 'dedicated-mobile') {
    return { serviceId, planId, country, quantity, period: formatTimePeriod(period), couponCode, originProxyId };
  }

  if (serviceId === 'static-datacenter-ipv4') {
    return {
      serviceId,
      couponCode,
      planId,
      quantity,
      region,
      city,
      country,
      period: formatTimePeriod(period),
      autoExtend: { isEnabled: isAutoExtendEnabled, traffic: null },
      ...(planId === 'basic' && { traffic: additionalBandwidth }),
      originProxyId,
    };
  }

  if (serviceId === 'static-datacenter-ipv6') {
    return {
      serviceId,
      couponCode,
      planId,
      region,
      city,
      country,
      packageId: proxyPackage ?? '50',
      period: formatTimePeriod(period),
      originProxyId,
    };
  }

  if (serviceId === 'static-residential-ipv6') {
    return {
      serviceId,
      couponCode,
      planId,
      region,
      city,
      country,
      packageId: proxyPackage ?? '50',
      period: formatTimePeriod(period),
      originProxyId,
    };
  }

  return {
    serviceId,
    couponCode,
    planId,
    quantity,
    region,
    city,
    country,
    ispId: isp,
    period: formatTimePeriod(period),
    autoExtend: { isEnabled: isAutoExtendEnabled, traffic: null },
    ...(planId === 'basic' && { traffic: additionalBandwidth }),
    originProxyId,
    ...(ipQualityScore != null && ipQualityVendor && { ipQuality: { vendor: ipQualityVendor, score: ipQualityScore } }),
  };
}

// eslint-disable-next-line @typescript-eslint/max-params
export function toOrderExecutePayload(
  from: OrderSetupFormModel,
  method?: PaymentOption,
  metadata?: Nullable<string>,
  params?: string,
  transactionId = `T_${Date.now()}`,
): OrderExecutePayload {
  return {
    ...toOrderSetupPayload(from),
    params,
    transactionId,
    payment: { method, metadata: metadata ?? undefined },
  };
}
// #endregion Payloads
